import { Icon } from '@billy/design-system'
import { Button } from '@shinetools/lumen-react'
import { ReactElement } from 'react'

import { useBalanceConfig, useBalanceLabels } from '@features/balanceLedger'

import type { PeriodMode } from '../../../../enums/periodMode'
import { useSuppliersBalanceFiltersContext } from '../../contexts/suppliersBalanceFiltersContext'
import { SuppliersBalanceFilterType } from '../../enums/suppliersBalanceFilterType'
import { useTrackFilterOpened } from '../../hooks/useTrackFilterOpened'
import { AddFilterMenu } from '../AddFilterMenu'
import { AccountsFilterChip } from './filters/AccountsFilterChip'
import { BalanceDirectionFilterChip } from './filters/BalanceDirectionFilterChip'
import { BarRow, Spacer } from './styles'

type SuppliersBalanceFiltersBarProps = {
  encodedPeriod?: string
  periodType?: PeriodMode
}

export const SuppliersBalanceFiltersBar = ({
  encodedPeriod,
  periodType,
}: SuppliersBalanceFiltersBarProps): ReactElement | null => {
  const { filterTypeOrder } = useBalanceConfig()
  const { bt } = useBalanceLabels()
  const trackFilterOpened = useTrackFilterOpened({ periodType })
  const { isBarVisible, activeTypes, addFilter, resetAll, addedTypes } = useSuppliersBalanceFiltersContext()

  if (!isBarVisible) {
    return null
  }

  const availableTypes = filterTypeOrder.filter(
    (type) =>
      !activeTypes.includes(type as unknown as SuppliersBalanceFilterType) &&
      !addedTypes.includes(type as unknown as SuppliersBalanceFilterType),
  ) as unknown as SuppliersBalanceFilterType[]
  const visibleTypes = filterTypeOrder.filter(
    (type) =>
      activeTypes.includes(type as unknown as SuppliersBalanceFilterType) ||
      addedTypes.includes(type as unknown as SuppliersBalanceFilterType),
  ) as unknown as SuppliersBalanceFilterType[]

  const renderChip = (type: SuppliersBalanceFilterType): ReactElement => {
    switch (type) {
      case SuppliersBalanceFilterType.Account:
        return <AccountsFilterChip key={type} encodedPeriod={encodedPeriod} />
      case SuppliersBalanceFilterType.BalanceDirection:
        return <BalanceDirectionFilterChip key={type} />
    }
  }

  return (
    <BarRow>
      {visibleTypes.map(renderChip)}
      <AddFilterMenu availableTypes={availableTypes} onOpen={trackFilterOpened} onSelect={addFilter} />
      <Spacer />
      <Button
        label={bt('filters.action.reset')}
        onClick={resetAll}
        size="small"
        startIcon={<Icon color="currentColor" icon="refresh" size="sm" />}
        variant="text"
      />
    </BarRow>
  )
}
